

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Video, ThumbsUp, Reply, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VideoResponse } from "./VideoResponse";
import { useVideoResponses } from "@/hooks/useVideoResponses";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { LazyVideo } from "./LazyVideo";

interface VideoResponsesProps {
  postId: string;
  farmName: string;
  hideAddResponse?: boolean;
}

export const VideoResponses = ({ postId, farmName, hideAddResponse = false }: VideoResponsesProps) => {
  const [showVideoResponse, setShowVideoResponse] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteResponseId, setDeleteResponseId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { videoResponses, loading, createVideoResponse, toggleLike, deleteVideoResponse } = useVideoResponses(postId);
  const { toast } = useToast();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  const handleVideoResponse = async (response: { video: string; text: string }) => {
    try {
      await createVideoResponse(postId, response.video, response.text);
      setShowVideoResponse(false);
    } catch (error) {
      console.error('Error creating video response:', error);
    }
  };

  const confirmDelete = async () => {
    if (deleteResponseId) {
      setIsDeleting(true);
      try {
        await deleteVideoResponse(deleteResponseId);
      } finally {
        setIsDeleting(false);
        setDeleteResponseId(null);
      }
    }
  };

  const handleDeleteResponse = (responseId: string) => {
    setDeleteResponseId(responseId);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="text-center py-4 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-xs mt-2">Loading responses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Add Response Button - Only show if not hidden */}
      {!hideAddResponse && (
        <div className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
          <div className="text-xs text-purple-800">
            <span className="font-medium">💬 Join the conversation</span> - Share your experience
          </div>
          <Button
            onClick={() => setShowVideoResponse(true)}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white h-7 px-2 text-xs"
          >
            <Video size={12} className="mr-1" />
            Add Response
          </Button>
        </div>
      )}

      {/* Responses List */}
      <div className="space-y-2">
        {videoResponses.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <Video className="mx-auto mb-1 text-gray-400" size={24} />
            <p className="text-xs">No responses yet</p>
            <p className="text-xs mt-1">Be the first to share your experience!</p>
          </div>
        ) : (
          videoResponses.map((response) => (
            <div key={response.id} className="bg-gray-50 rounded p-2 border border-gray-200">
              {/* Response Header */}
              <div className="flex items-start space-x-2 mb-2">
                <img
                  src={response.profiles?.avatar_url || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"}
                  alt={response.profiles?.farm_name || response.profiles?.username || "User"}
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1 text-xs">
                    <span className="font-medium text-gray-900 truncate">
                      {response.profiles?.farm_name || response.profiles?.username || "Anonymous User"}
                    </span>
                    <span className="text-gray-500 truncate">
                      @{response.profiles?.username || "unknown"}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-400 flex-shrink-0">
                      {new Date(response.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                {currentUserId === response.user_id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-0.5 h-auto">
                        <MoreHorizontal size={12} className="text-gray-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteResponse(response.id)}
                        className="text-red-600"
                      >
                        <Trash2 size={12} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Video */}
              <div className="mb-2">
                <LazyVideo
                  src={response.video_url}
                  className="w-full h-24 object-cover rounded"
                  width={320}
                  height={96}
                  controls={true}
                  muted
                  postId={response.id}
                />
              </div>

              {/* Response Text */}
              {response.response_text && (
                <p className="text-xs text-gray-800 mb-2 line-clamp-2">{response.response_text}</p>
              )}

              {/* Response Actions */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => toggleLike(response.id)}
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-xs transition-colors ${
                    response.is_liked
                      ? "bg-red-50 text-red-600"
                      : "hover:bg-gray-100 text-gray-600"
                  }`}
                >
                  <ThumbsUp size={10} className={response.is_liked ? "fill-current" : ""} />
                  <span>{response.likes_count}</span>
                </button>
                <button className="flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-xs hover:bg-gray-100 text-gray-600 transition-colors">
                  <Reply size={10} />
                  <span>Reply</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Video Response Modal - Rendered via Portal */}
      {showVideoResponse && createPortal(
        <VideoResponse
          postId={postId}
          farmName={farmName}
          onClose={() => setShowVideoResponse(false)}
          onSubmit={handleVideoResponse}
        />,
        document.body
      )}

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Video Response"
        description="Are you sure you want to delete this video response? This action cannot be undone and the response cannot be restored."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
        loading={isDeleting}
      />
    </div>
  );
};

